/* ═══════════════════════════════════════════════════════════════
   EmptyState — reusable "no content" / "no data" placeholder.

   Use when a surface has nothing meaningful to show (no rows, no
   data in range, no results) instead of rendering a blank frame or
   alarming zero-value noise. Satisfies the `empty-states` and
   `empty-data-state` rules — see docs/DESIGN.md §5.

   Props:
     - icon?    : inline SVG node (defaults to a neutral chart glyph).
                  Never an emoji — rule `no-emoji-icons`, docs/DESIGN.md §5.
     - title    : short headline ("No time logged in this range").
     - subtitle?: one-line hint toward a fix ("Try a wider date range").
     - action?  : optional CTA { label, onClick }.
     - compact? : tight variant for short WidgetGrid cells (Phase health,
                  Hours budget, AR aging, Cost budget) — no forced
                  min-height (the message would scroll out of view in a
                  short cell), tighter spacing, smaller (em) icon, base
                  title size, action rendered as a text button. Used by
                  WidgetFrame for both its empty and error states.

   Styling uses the app's semantic CSS variables only:
     --sf surface · --bd border · --tx text · --tm muted text
     --ac accent · --acr accent rgb triple.
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import { rem } from '../lib/rem';

export interface EmptyStateAction {
  label: string;
  onClick: () => void;
}

export interface EmptyStateProps {
  icon?: React.ReactNode;
  title: string;
  subtitle?: string;
  action?: EmptyStateAction;
  compact?: boolean;
}

// Neutral default glyph: a bar-chart outline. Inline SVG, currentColor,
// so it inherits the muted accent tint below. No emoji. Sized in `em` so
// it follows the wrapper circle's font-size (set per compact/standard).
const DefaultIcon = (
  <svg
    width="1em"
    height="1em"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.75"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    <path d="M3 3v18h18" />
    <rect x="7" y="12" width="3" height="5" rx="0.5" />
    <rect x="13" y="8" width="3" height="9" rx="0.5" />
    <rect x="19" y="14" width="0.01" height="0.01" />
  </svg>
);

export default function EmptyState({ icon, title, subtitle, action, compact }: EmptyStateProps) {
  return (
    <div
      role="status"
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        gap: compact ? 'var(--space-1)' : rem(6),
        padding: compact ? 'var(--space-2) var(--space-2)' : 'var(--space-7) var(--space-5)',
        minHeight: compact ? 0 : rem(200),
        width: '100%',
        background: 'var(--sf)',
        border: '1px dashed var(--bd)',
        borderRadius: rem(8),
      }}
    >
      <div
        aria-hidden="true"
        style={{
          width: compact ? '1.5em' : '3.25em',
          height: compact ? '1.5em' : '3.25em',
          fontSize: compact ? '1em' : '1.25em',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: '50%',
          marginBottom: compact ? 0 : rem(6),
          background: 'rgba(var(--acr), 0.10)',
          color: 'var(--ac)',
        }}
      >
        {icon ?? DefaultIcon}
      </div>

      <div
        style={{
          fontSize: compact ? 'var(--font-size-base)' : 'calc(var(--font-size-base) * 15 / 13)',
          fontWeight: 700,
          color: 'var(--tx)',
        }}
      >
        {title}
      </div>

      {subtitle && (
        <div
          style={{
            fontSize: 'var(--font-size-sm)',
            color: 'var(--tm)',
            maxWidth: rem(360),
            lineHeight: 1.5,
          }}
        >
          {subtitle}
        </div>
      )}

      {action &&
        (compact ? (
          <button
            type="button"
            onClick={action.onClick}
            style={{
              marginTop: 'var(--space-1)',
              padding: 0,
              border: 'none',
              background: 'none',
              color: 'var(--ac)',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 600,
              cursor: 'pointer',
              textDecoration: 'underline',
            }}
          >
            {action.label}
          </button>
        ) : (
          <button
            type="button"
            onClick={action.onClick}
            style={{
              marginTop: rem(14),
              padding: 'var(--space-2) var(--space-4)',
              borderRadius: 'var(--radius-md)',
              border: '1px solid var(--ac)',
              background: 'var(--ac)',
              color: '#fff',
              fontSize: 'var(--font-size-base)',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            {action.label}
          </button>
        ))}
    </div>
  );
}
